import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    ReactiveFormChangedField,
    ReactiveComputedFieldContext,
    ReactiveFormValue,
    type ReactiveDropdownDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { SectoresEnvironmentDefinition } from '../environment-variables';
import { SectoresListsDefinition } from './list-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import {
  ReactiveDomainSectionFactory,
  ReactiveHiddenFieldFactory,
  ReactiveSelectionResolver,
  type ReactiveSelectionResolverConfiguration,
} from '@fitogest/reactive-form-builder/utils';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';


const FINCA_SELECTION_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
  idKeys: ['id', 'id_finca', 'nombre', 'finca'],
};

export interface SectoresDropdownEndpoints {
  readonly fincas?: unknown;
  readonly jardines?: unknown;
}

export class SectoresFormDefinition {
  public formType!: FormType;

  public env: SectoresEnvironmentDefinition = new SectoresEnvironmentDefinition();

  public lists: SectoresListsDefinition = new SectoresListsDefinition();

  public usuario = StorageManager.getUser();

  public cliente = StorageManager.getClient();

  public model: ReactiveFormValue<SectorModel> = {} as ReactiveFormValue<SectorModel>;

  public tag: ReactiveFormChangedField<SectorModel> = '';

  public formFieldsSection!: FormSection<object>;

  public formFields!: Form<SectorModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];


  constructor(
    type: FormType,
    _model: Record<string,string>,
    table: (string[] | string[][])[] | null,
    private readonly dropdownEndpoints: SectoresDropdownEndpoints = {}
  ) {
    this.formType = +type;

    const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

    this.formFields = {
      name: 'sectores',
      header: action + ' ' + this.env.sectorName,
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1]},
      uploadButton: {
        isEnabled: false,
        label: ''
      },
      signed: false,
    };
  }


  private createFincasDataSourceField(): Partial<Pick<FormField<SectorModel>, 'dataSource'>> {
    const endpoint = this.dropdownEndpoints.fincas;

    if (endpoint === undefined || endpoint === null) {
      return {};
    }

    const dataSource: ReactiveDropdownDataSource<SectorModel> = {
      endpoint,
      cacheKey: 'sectores-fincas',
      labelKey: 'nombre',
      valueKey: 'id',
      distinctBy: 'id',
      cacheEmptyResults: false,
      mapOption: (item: unknown) => {
        const finca = item as Partial<FincasModel>;

        return {
          label: String(finca.nombre ?? finca.id ?? ''),
          value: item as FincasModel,
          raw: item,
        };
      },
    };

    return { dataSource };
  }

  private createJardinesDataSourceField(): Partial<Pick<FormField<SectorModel>, 'dataSource'>> {
    const endpoint = this.dropdownEndpoints.jardines;

    if (this.env.appName !== 'detorres' || endpoint === undefined || endpoint === null) {
      return {};
    }

    const dataSource: ReactiveDropdownDataSource<SectorModel> = {
      endpoint,
      cacheKey: 'sectores-jardines',
      labelKey: 'nombre',
      valueKey: 'id',
      distinctBy: 'id',
      cacheEmptyResults: false,
      mapOption: (item: unknown) => {
        const jardin = item as Partial<ParcelasModel>;

        return {
          label: String(jardin.nombre ?? jardin.id ?? ''),
          value: item as ParcelasModel,
          raw: item,
        };
      },
    };

    return { dataSource };
  }

  public getFormFields(): FormField<any>[] {
    const formFields: FormField<any>[] = [
      {
          name: this.formType < 3 ? 'id_finca' : 'finca',
          label: this.env.fincaName,
          input: {
            type: InputType.DROPDOWN,
            isEditable: true,
            data: this.lists.fincas,
            dropdownConfiguration: {valuePrimaryKey: 'id'}
          },
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: true,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true
          },
          order: 1,
          ...this.createFincasDataSourceField()
        },
        {
          name: 'nombre',
          label: `Nombre ${this.env.sectorName}`,
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: true,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true
          },
          order: 2
        },
        {
          name: this.formType < 3 ? 'id_jardin' : 'jardin',
          label: 'Jardín',
          input: {
            type: InputType.DROPDOWN,
            isEditable: true,
            data: this.lists.jardines,
            dropdownConfiguration: {valuePrimaryKey: 'id'}
          },
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName === 'detorres'
          },
          order: 3,
          ...this.createJardinesDataSourceField()
        },
        {
          name: 'partida',
          label: 'Partida',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.showPartida
          },
          order: 4
        },
        {
          name: 'goteros',
          label: 'Nº Goteros/Árbol',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest'
          },
          order: 5
        },
        {
          name: 'caudal_gotero',
          label: 'Caudal Gotero (l/h)',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest'
          },
          order: 6
        },
        {
          name: 'caudal_cuba',
          label: 'Capacidad Cuba (l)',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest'
          },
          order: 7
        },
        {
          name: 'caudal_inyectora',
          label: 'Caudal Inyectora (l/h)',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest'
          },
          order: 8
        },
        {
          name: 'distancia_goteros',
          label: 'Distancia entre Goteros',
          input: {
            type: InputType.TEXT,
            isEditable: true,
            data: this.lists.distanciasGoteros
          },
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName === 'benibal'
          },
          order: 9
        },
        {
          name: 'sup_cultivada',
          label: 'S.\xa0cultivada\xa0(' + this.env.textoSuperficie + ')',
          input: {type: InputType.TEXT, isEditable: true},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest' && this.formType >= 3
          },
          order: 10
        },
        {
          name: 'total_plantas',
          label: 'Nº\xa0Plantas',
          input: {type: InputType.TEXT, isEditable: false},
          width: 3,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: this.env.appName !== 'gardengest' && this.formType >= 3
          },
          order: 11
        },
    ];

    this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<SectorModel>[] = [
      ReactiveHiddenFieldFactory.create<SectorModel>({
        name: 'id',
        defaultValue: this.model.id,
        width: 2,
        order: 1,
        conditions: { rememberedOnDuplicate: true },
      }),
      ReactiveHiddenFieldFactory.create<SectorModel>({
        name: 'id_cliente',
        defaultValue: this.resolveClientId(),
        width: 2,
        order: 2,
        conditions: { rememberedOnDuplicate: true, visible: this.env.hasClients },
        computed: {
          dependsOn: ['id_finca', 'finca'],
          resolve: (context: ReactiveComputedFieldContext<SectorModel>) => this.resolveClientId(context),
        },
      }),
      ReactiveHiddenFieldFactory.create<SectorModel>({
        name: 'id_usuario',
        defaultValue: this.resolveUserId(),
        order: 3,
        conditions: { rememberedOnDuplicate: true },
        computed: {
          dependsOn: ['id_finca', 'finca'],
          resolve: () => this.resolveUserId(),
        },
      }),
    ];

    this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  private resolveClientId(context?: ReactiveComputedFieldContext<SectorModel>): string | number {
    if (this.cliente?.id !== null && this.cliente?.id !== undefined) {
      return this.cliente.id;
    }

    return this.resolveSelectedFinca(context)?.id_cliente ?? '';
  }

  private resolveUserId(): string | number {
    if (this.env.applicationType === 'cropgest') {
      return this.cliente?.id_usuario ?? this.usuario.id;
    }

    return this.cliente?.id_usuario_asignado ?? this.model.id_usuario ?? this.usuario.id;
  }

  private resolveSelectedFinca(context?: ReactiveComputedFieldContext<SectorModel>): FincasModel | null {
    const selectedValue = ReactiveSelectionResolver.readContextValue(context, 'id_finca')
      ?? ReactiveSelectionResolver.readContextValue(context, 'finca')
      ?? this.model.id_finca
      ?? this.model.finca;

    return ReactiveSelectionResolver.resolveSelectedEntities(
      this.lists.fincas,
      selectedValue,
      FINCA_SELECTION_CONFIGURATION,
    )[0] ?? null;
  }


  private getFormSections(): FormSection<any>[] {
    const sections: FormSection<SectorModel>[] = [
      ReactiveDomainSectionFactory.localizacion<SectorModel>({
        name: '',
        fields: this.getFormFields(),
        order: 1,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<SectorModel>({
        name: '',
        fields: this.getHiddenFields(),
        order: 2,
      }),
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
}